export type FriendlyAppNameInput = {
  displayName?: string | null;
  appIdentifier?: string | null;
  category?: string | null;
};

import { resolveLocalSvgIcon } from "./resolveLocalSvgIcon";
import { resolveAppleSystemApp } from "./appleSystemApps";
import { IOS_IN_CALL_DISPLAY_NAME, isIosInCallService } from "./iosInCallApp";
import { IOS_SCREENSHOT_DISPLAY_NAME, isIosScreenshotBundleId } from "./iosScreenshotApp";
import {
  rankIdentifierSignalTokens,
  tokenizeAppSegments,
} from "./packageSegmentStopwords";

const REVERSE_DOMAIN_PREFIX = /^(com|org|net|io|edu|gov|app|dev|me|co|uk|de|fr|it)\./i;
const WEB_DOMAIN_SUFFIX = /\.(com|org|net|io|edu|gov|app|dev|me|co|uk|de|fr|it)$/i;
const PACKAGE_SEGMENT = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

/** Trivial brand fixes for last-segment derivation (not a full icon/name catalog). */
const KNOWN_SEGMENT_LABELS: Record<string, string> = {
  mobilesafari: "Safari",
  oneconnect4ios: "SmartThings",
  mobilemail: "Mail",
  saapp2499363: "StrongAuth Mobile",
  youtube: "YouTube",
  bookingapp: "Booking.com",
  preferences: "Settings",
  aivapp: "Prime Video",
};

const KNOWN_DISPLAY_NAME_LABELS: Record<string, string> = {
  preferences: "Settings",
  freedomblocker: "Freedom",
  "one-sec": "One Sec",
  aivapp: "Prime Video",
  cbs: "Paramount+",
};

const KNOWN_IDENTIFIER_LABELS: Record<string, string> = {
  "com.apple.mobilesafari": "Safari",
  "com.apple.mobilemail": "Mail",
  "com.apple.preferences": "Settings",
  "com.google.maps": "Google Maps",
  "com.microsoft.skype.teams": "Teams",
  "com.samsung.oneconnect4ios": "SmartThings",
  "com.spotify.client": "Spotify",
  "com.openai.chat": "ChatGPT",
  "com.openai.chatgpt": "ChatGPT",
  "com.booking.bookingapp": "Booking.com",
  "net.skyscanner.iphone": "Skyscanner",
  "com.withopal.opal": "Opal",
  "wtf.riedel.one-sec": "One Sec",
  "to.freedom.freedomblocker": "Freedom",
  "com.amazon.aiv.aivapp": "Prime Video",
  "com.cbs.canada.app": "Paramount+",
};

export function isIdentifierShaped(value: string, appIdentifier?: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;

  const trimmedId = appIdentifier?.trim();
  if (trimmedId && trimmed === trimmedId) return true;

  if (!trimmed.includes(".")) return false;

  if (REVERSE_DOMAIN_PREFIX.test(trimmed)) return true;

  const segments = trimmed.split(".");
  if (segments.length >= 2 && segments.every((segment) => PACKAGE_SEGMENT.test(segment))) {
    return true;
  }

  return false;
}

function presentSegment(segment: string): string {
  const trimmed = segment.trim();
  if (!trimmed) return "Unknown app";

  const known = KNOWN_SEGMENT_LABELS[trimmed.toLowerCase()];
  if (known) return known;

  if (trimmed.length <= 5 && trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed)) {
    return trimmed;
  }

  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function formatLastSegment(identifier: string): string {
  const segment = identifier.trim().split(".").pop() ?? "";
  return presentSegment(segment);
}

function formatBestSignalSegment(identifier: string): string {
  const segments = tokenizeAppSegments(identifier);
  const lastSegment = segments.at(-1) ?? "";
  const knownLastSegment = lastSegment ? KNOWN_SEGMENT_LABELS[lastSegment] : undefined;
  if (knownLastSegment) return knownLastSegment;

  const rankedSignals = rankIdentifierSignalTokens(identifier);
  if (rankedSignals.length > 0) return presentSegment(rankedSignals[0]!);

  return formatLastSegment(identifier);
}

function isWebsiteCategory(category?: string | null): boolean {
  return category?.toLowerCase() === "website";
}

function isLikelyWebDomain(value: string): boolean {
  return WEB_DOMAIN_SUFFIX.test(value.trim()) && !REVERSE_DOMAIN_PREFIX.test(value.trim());
}

export type FriendlyAppNameOptions = {
  /**
   * Cached App Store `trackName` for this app's iOS bundle ID. When present it
   * wins over every existing heuristic (apple system apps, manual maps, etc.).
   * Callers pass this only when the App Store lookup actually resolved.
   */
  appStoreName?: string | null;
};

export function friendlyAppName(
  { displayName, appIdentifier, category }: FriendlyAppNameInput,
  options?: FriendlyAppNameOptions,
): string {
  // Step 1 (App Store cache): a resolved App Store name wins over local heuristics.
  const appStoreName = options?.appStoreName?.trim();
  if (appStoreName) return appStoreName;

  const trimmedDisplay = (displayName ?? "").trim();
  const trimmedId = (appIdentifier ?? "").trim();
  const localSvg = resolveLocalSvgIcon({ displayName: trimmedDisplay, appIdentifier: trimmedId });

  if (isWebsiteCategory(category)) {
    return trimmedDisplay || trimmedId.replace(/^web:/i, "") || "Unknown website";
  }

  if (isIosScreenshotBundleId(trimmedId, trimmedDisplay)) {
    return IOS_SCREENSHOT_DISPLAY_NAME;
  }

  if (isIosInCallService(trimmedId, trimmedDisplay)) {
    return IOS_IN_CALL_DISPLAY_NAME;
  }

  const appleSystemApp = resolveAppleSystemApp({
    displayName: trimmedDisplay,
    appIdentifier: trimmedId,
  });
  if (appleSystemApp) return appleSystemApp.officialDisplayName;

  const knownIdentifier = KNOWN_IDENTIFIER_LABELS[trimmedId.toLowerCase()];
  if (knownIdentifier) return knownIdentifier;

  if (trimmedDisplay && isLikelyWebDomain(trimmedDisplay)) {
    return trimmedDisplay;
  }

  if (trimmedDisplay && !isIdentifierShaped(trimmedDisplay, trimmedId)) {
    const displayLabel = KNOWN_DISPLAY_NAME_LABELS[trimmedDisplay.toLowerCase()];
    if (displayLabel) return displayLabel;
    if (localSvg) return localSvg.label;

    if (trimmedDisplay === trimmedDisplay.toLowerCase() && /^[a-z0-9][a-z0-9_-]*$/.test(trimmedDisplay)) {
      return presentSegment(trimmedDisplay);
    }
    return trimmedDisplay;
  }

  const identifierSource = trimmedId || trimmedDisplay;
  if (identifierSource && isIdentifierShaped(identifierSource, trimmedId || identifierSource)) {
    if (localSvg) return localSvg.label;
    return formatBestSignalSegment(identifierSource);
  }

  if (trimmedDisplay) {
    if (localSvg) return localSvg.label;
    return presentSegment(trimmedDisplay);
  }

  return "Unknown app";
}
